import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { buildDashboardSnapshot } from "./utils/buildDashboardSnapshot";
import { sampleDashboard } from "./data/sampleDashboard";
import StreakCalendar from "./modules/streaks/StreakCalendar";

import AILightComponent from "../../components/AILightComponent";

const sampleTodoStreak = [
  // example shape – adjust to whatever StreakCalendar expects
  { todoDate: "2026-07-28", status: "done" },
  { todoDate: "2026-07-29", status: "done" },
  { todoDate: "2026-07-30", status: "done" },
  { todoDate: "2026-07-31", status: "planned" },
  { todoDate: "2026-08-01", status: "done" },
  { todoDate: "2026-08-02", status: "done" },
  { todoDate: "2026-08-03", status: "done" },
];

const defaultDashboard = {
    score: 0,
    zone: "Starting",
    streak: { current: 0 },
    stats: {
        total: 0,
        done: 0,
        planned: 0,
        cancelled: 0,
    },
    trend: [],
    objectives: {
        distribution: {},
    },
    insights: [],
    recommendedAction: "",
};

const ProgressBar = ({ value = 0, color = "from-cyan-400 to-green-400" }) => (
    <div className="h-2 w-full bg-[#111827] rounded-full overflow-hidden">
        <div
            className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-700`}
            style={{ width: `${Math.min(value, 100)}%` }}
        />
    </div>
);

const SignalCard = ({
    emoji,
    title,
    subtitle,
    value,
    valueColor = "text-cyan-300",
}) => (
    <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
        <div className="text-xl">{emoji}</div>

        <div className="text-sm font-bold mt-2">{title}</div>

        <div className="text-[11px] text-gray-100 mt-1">{subtitle}</div>

        <div className={`mt-3 text-xs font-bold ${valueColor}`}>
            {value}
        </div>
    </div>
);

const InsightCard = ({
    emoji,
    title,
    text,
    border = "border-cyan-500/20",
}) => (
    <div className={`bg-[#111827] rounded-2xl p-4 border ${border}`}>
        <div className="flex items-start gap-3">
            <div className="text-xl">{emoji}</div>

            <div>
                <div className="text-xs font-black text-white">{title}</div>

                <div className="text-xs text-gray-100 mt-2 leading-6">
                    {text}
                </div>
            </div>
        </div>
    </div>
);

const DashboardSkeleton = () => (
    <div className="space-y-3 animate-pulse">
        <div className="h-44 rounded-3xl bg-[#1f2937]" />

        <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
                <div
                    key={i}
                    className="h-20 rounded-2xl bg-[#1f2937]"
                />
            ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
                <div
                    key={i}
                    className="h-32 rounded-2xl bg-[#1f2937]"
                />
            ))}
        </div>
    </div>
);

const PDDashboard = ({ useSample, go, isActiveSub, startDate, endDate }) => {

    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();

    const userId = authState?.user?._id;

    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState(null);

    const [todoStreak, setTodoStreak] = useState([]);
    // const [roachStreak, setRoachStreak] = useState([]);

    const [enableAIInsight, setEnableAIInsight] = useState(false);
    const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);

    const [ownerSettings, setOwnerSettings] = useState(null);
    const [eligibleMilestones, setEligibleMilestones] = useState([]);
    const [requestMsg, setRequestMsg] = useState(null);
    const [calendarStreak, setCalendarStreak] = useState(0);

    const d = useMemo(() => {
        if (useSample) {
            return sampleDashboard;
        }
        return dashboard || defaultDashboard;
    }, [dashboard, useSample]);

    const toggleAIInsight = () => setEnableAIInsight((p) => !p);
    const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

    const fetchDashboard = async () => {
        try {
            setLoading(true);

            const config = {
                url: "/pd/my/todos/raw",
                method: "post",
                data: {
                    userId,
                    startDate: startDate,
                    endDate: endDate,
                },
                headers: { Authorization: authState.token,},
            };
            const response = await fetchData( config, { silent: true });
            
            if (response?.status) {
                const rawTodos = response?.todos || [];
                setTodoStreak(rawTodos);

                const report = buildDashboardSnapshot({
                    todos: rawTodos
                });
                setDashboard(report);
            }
        } catch (error) {

            console.error("Dashboard Error:", error);

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {
        if (useSample) return;
        if (userId && startDate && endDate) {
            fetchDashboard();
        }
    }, [userId, startDate, endDate, useSample ]);

    const fetchMilestoneEligibility = async (currentStreak) => {
        try {
            const settingsRes = await fetchData({
                url: "/pd/streaks/settings-get",
                method: "post",
                data: { targetId: userId },
                headers: { Authorization: authState.token },
            });
            const settings = settingsRes?.data?.[0] || settingsRes?.data || null;
            if (!settings) return;
            setOwnerSettings(settings);

            const eligRes = await fetchData({
                url: "/pd/streaks/milestone-eligibility",
                method: "post",
                data: { targetId: userId, ownerId: settings.ownerId, currentStreak },
                headers: { Authorization: authState.token },
            });
            if (eligRes?.success) setEligibleMilestones(eligRes.eligible || []);
        } catch (err) {
            console.error("Milestone eligibility error:", err);
        }
    };

    // useEffect(() => {
    //     if (userId && d.streak) fetchMilestoneEligibility(d.streak.current || 0);
    // }, [userId, d.streak?.current]);
    useEffect(() => {
        if (userId) fetchMilestoneEligibility(calendarStreak);
    }, [userId, calendarStreak]);

    const handleRequestApproval = async (days) => {
        try {
            setRequestMsg(null);
            const res = await fetchData({
                url: "/pd/streaks/claim-create-milestone",
                method: "post",
                data: {
                    customerId: userId,
                    customerCode: authState?.user?.think5whyscode,
                    customerName: authState?.user?.name,
                    ownerId: ownerSettings?.ownerId,
                    ownerCode: ownerSettings?.ownerCode,
                    ownerName: ownerSettings?.ownerName,
                    ownerType: ownerSettings?.ownerType,
                    streakDuration: `${days}D`,
                    periodStartDate: startDate,
                },
                headers: { Authorization: authState.token },
            });
            if (res?.success) {
                setRequestMsg({ type: "success", text: `${days}-day approval request sent!` });
                setEligibleMilestones((p) => p.filter((d2) => d2 !== days));
            } else {
                setRequestMsg({ type: "warning", text: res?.message || "Request failed" });
            }
        } catch (err) {
            setRequestMsg({ type: "error", text: "Request failed" });
        }
    };

    // if (loading && !dashboard) return <DashboardSkeleton />;
    if ( !useSample && loading && !dashboard) {return <DashboardSkeleton />;}

    const handleShareDashboard = () => {
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();

        let y = 20;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(20);
        pdf.text("Think5Whys PurpleDoor Dashboard", pageWidth / 2, y, {
            align: "center",
        });

        y += 10;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");

        pdf.text(
            `Generated On: ${new Date().toLocaleString()}`,
            15,
            y
        );

        y += 20;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Name: ${authState?.user?.name}`, 15, y);

        y += 12;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("Performance Summary", 15, y);

        y += 8;

        pdf.setFontSize(11);
        pdf.setFont("helvetica", "normal");

        pdf.text(`Momentum Score: ${d.score}/100`, 15, y);
        y += 7;

        pdf.text(`Zone: ${d.zone}`, 15, y);
        y += 7;

        pdf.text(
            `Current Streak: ${d.streak?.current || 0}`,
            15,
            y
        );

        y += 12;

        pdf.setFont("helvetica", "bold");
        pdf.text("Task Statistics", 15, y);

        y += 8;

        pdf.setFont("helvetica", "normal");

        pdf.text(`Total Tasks: ${d.stats?.total || 0}`, 15, y);
        y += 7;

        pdf.text(`Completed: ${d.stats?.done || 0}`, 15, y);
        y += 7;

        pdf.text(`Planned: ${d.stats?.planned || 0}`, 15, y);
        y += 7;

        pdf.text(`Cancelled: ${d.stats?.cancelled || 0}`, 15, y);

        y += 12;

        pdf.setFont("helvetica", "bold");
        pdf.text("Behavior Signals", 15, y);

        y += 8;

        pdf.setFont("helvetica", "normal");

        pdf.text(
            `Focus: ${d.signals?.focus || 0}%`,
            15,
            y
        );

        y += 7;

        pdf.text(
            `Consistency: ${d.signals?.consistency || 0}%`,
            15,
            y
        );

        y += 7;

        pdf.text(
            `Discipline: ${d.signals?.discipline || 0}%`,
            15,
            y
        );

        y += 7;

        pdf.text(
            `Resilience: ${d.signals?.resilience || 0}%`,
            15,
            y
        );

        y += 12;

        pdf.setFont("helvetica", "bold");
        pdf.text("Behavior Profile", 15, y);

        y += 8;

        // pdf.setFont("helvetica", "normal");

        // pdf.text(
        //     `Roach Type: ${d.behavior?.roachType || "Unknown"}`,
        //     15,
        //     y
        // );

        // y += 12;

        pdf.setFont("helvetica", "bold");
        pdf.text("AI Insights", 15, y);

        y += 8;

        pdf.setFont("helvetica", "normal");

        (d.insights || []).slice(0, 5).forEach((item) => {

            const lines = pdf.splitTextToSize(
                `• ${item}`,
                175
            );

            pdf.text(lines, 15, y);

            y += lines.length * 6 + 2;
        });

        if (d.recommendedAction) {

            y += 4;

            pdf.setFont("helvetica", "bold");

            pdf.text("Recommended Action", 15, y);

            y += 8;

            pdf.setFont("helvetica", "normal");

            const lines = pdf.splitTextToSize(
                d.recommendedAction,
                175
            );

            pdf.text(lines, 15, y);
        }

        pdf.save(
            `PurpleDoorDashboard-${new Date()
                .toISOString()
                .split("T")[0]}.pdf`
        );
    };

    const calendarData = useSample ? sampleTodoStreak : todoStreak;

    // safe fallbacks so StreakCalendar never gets null/undefined dates
    const safeStart = startDate || "2026-07-01";
    const safeEnd   = endDate   || "2026-08-31";

    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-yellow-400 mt-2">
            {useSample && (
                <div className="space-y-6 bg-gray-700 p-6 rounded-xl text-white border-t-2 border-blue-600 mt-2">
                    📦 You are viewing SAMPLE DASHBOARD DATA (for demo purpose)
                </div>
            )}
            <div className="bg-[#1f2937] rounded-3xl p-5 border border-gray-800 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-500/10 blur-3xl rounded-full" />
                <div className="relative z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-lg font-black text-white">
                                🔥 Your Momentum
                            </div>
                            <div className="text-xs text-gray-100 mt-1">
                                Real-time transformation tracker
                            </div>
                            <div className="flex-1 flex-col w-full justify-center items-center">
                                <button
                                    onClick={toggleGenAIHelp}
                                    className={`w-fit px-1 py-1 rounded-sm text-[10px] font-bold transition-all ${
                                        enableGenAIHelp
                                            ? "bg-purple-500 text-white"
                                            : "bg-[#111827] text-purple-300 border border-purple-500/30"
                                    }`}
                                >
                                    {enableGenAIHelp
                                        ? "🤖 AI Assistant Active"
                                        : "🚀 Ask GenAI Coach"}
                                </button>
                                {enableGenAIHelp && (
                                    <div className="flex-1 justify-center items-center w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                                        <AILightComponent
                                            param1="PurpleDoor Live Dashboard Analysis"
                                            param2={`
                                                90-Day Score: ${d.score}/100
                                                Zone: ${d.zone}

                                                Please analyze:
                                                - Productivity pattern
                                                - Focus weakness
                                                - Behavioral growth
                                                - Burnout risk
                                                - Identity evolution
                                                - Suggested next 3 actions
                                            `}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xl lg:text-2xl font-black text-cyan-400">
                                {d.score}
                            </div>
                            <div className="text-[11px] l:text-sm text-gray-100">
                                /100
                            </div>
                        </div>
                    </div>

                    <div className="mt-2">
                        <div className="flex justify-between text-[11px] lg:text-sm text-gray-100 mb-2">
                            <span>Progress</span>
                            <span>{d.zone}</span>
                        </div>
                        <ProgressBar value={d.score} />
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-5">
                        <div className="bg-[#111827]/80 rounded-2xl p-3 text-center">
                            <div className="text-[10px] text-cyan-500">
                                Tasks
                            </div>
                            <div className="text-lg font-black text-white mt-1">
                                {d.stats?.total || 0}
                            </div>
                        </div>
                        <div className="bg-[#111827]/80 rounded-2xl p-3 text-center">
                            <div className="text-[10px] text-gray-200">
                                Completed
                            </div>
                            <div className="text-lg font-black text-green-400 mt-1">
                                {d.stats?.done || 0}
                            </div>
                        </div>
                        <div className="bg-[#111827]/80 rounded-2xl p-3 text-center">
                            <div className="text-[10px] text-gray-200">
                                Focus
                            </div>
                            <div className="text-lg font-black text-cyan-400 mt-1">
                                {d.stats?.focusRate || 0}%
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <StreakCalendar
                title="📋 Todo Streak Calendar"
                data={calendarData}
                startDate={safeStart}
                endDate={safeEnd}
                dateField="todoDate"
                eligibleMilestones={useSample ? [7, 14, 30] : eligibleMilestones}
                onRequestApproval={useSample ? () => {} : handleRequestApproval}
                onStreakChange={setCalendarStreak}
            />

            {requestMsg && (
                <div className={`text-xs p-2 rounded-xl ${requestMsg.type === "success" ? "bg-green-900/30 text-green-300" : "bg-yellow-900/30 text-yellow-300"}`}>
                    {requestMsg.text}
                </div>
            )}

            <div className="flex flex-row gap-2">
                <button
                    disabled={!isActiveSub}
                    onClick={() => go("daily")}
                    className="w-fit bg-blue-600 hover:bg-blue-500 transition-all px-2 py-1 rounded-xl text-xs font-black"
                >
                    🚀 Start Daily Check-in
                </button>
                <button
                    onClick={handleShareDashboard}
                    className="w-fit bg-green-600 hover:bg-green-500 transition-all px-2 py-1 rounded-xl text-xs font-black"
                >
                    📄 Share Dashboard Report
                </button>
            </div>
        </div>
    );
};

export default PDDashboard;